import { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getOrCreateSessionId } from '../../utility/sessionManager';
import { hasAnalyticsConsent } from '../../services/cookiePreferencesService';
import eventBus from '../../utility/eventBus';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsTracker } from './AnalyticsTracker';
import { ROUTES } from '../../resources/routes-constants';

/**
 * AnalyticsWrapper component that handles analytics tracking
 * Should be placed inside RouterProvider to access useLocation
 * Only initializes tracking if user has consented to analytics
 */
export const AnalyticsWrapper: React.FC = () => {
    const location = useLocation();
    const [hasConsent, setHasConsent] = useState(() => hasAnalyticsConsent());
    const [sessionId] = useState(() => getOrCreateSessionId());

    // Determine if current route is an admin route; admin pages should not send analytics
    const isAdminRoute = useMemo(() => {
        const adminRoutes = [
            ROUTES.ADMIN_USER_MANAGEMENT,
            ROUTES.ADMIN_DASHBOARD,
            ROUTES.ADMIN_TIER_MANAGEMENT,
            ROUTES.ADMIN_ANALYTICS,
        ];
        return adminRoutes.some((adminPath) => location.pathname.startsWith(adminPath));
    }, [location.pathname]);

    // Use stable URL for analytics
    const pageUrl = useMemo(() => {
        return `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
    }, [location.pathname, location.search, location.hash]);

    // Track the effective page title used for analytics, scoped to a specific URL.
    // This lets us avoid sending analytics for a new URL until we've captured
    // that URL's own title after per-page usePageTitle effects have run.
    const [pageTitleInfo, setPageTitleInfo] = useState<{ url: string; title: string | null }>(() => ({
        url: pageUrl,
        title: typeof document !== 'undefined' ? document.title : null,
    }));

    useEffect(() => {
        // Capture updated document.title for the *new* URL after the new page's effects run
        const timeoutId = window.setTimeout(() => {
            if (typeof document !== 'undefined') {
                setPageTitleInfo({
                    url: pageUrl,
                    title: document.title || null,
                });
            }
        }, 0);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [pageUrl]);

    // Listen for consent changes
    useEffect(() => {
        const handleConsentChange = (_event?: Event) => {
            setHasConsent(hasAnalyticsConsent());
        };

        // Initial check on mount
        handleConsentChange();

        // Listen for cross-tab changes
        window.addEventListener('storage', handleConsentChange as EventListener);

        // Listen for in-tab updates from CookieConsent
        eventBus.on('cookiePreferencesChanged', handleConsentChange as EventListener);

        return () => {
            window.removeEventListener('storage', handleConsentChange as EventListener);
            eventBus.remove('cookiePreferencesChanged', handleConsentChange as EventListener);
        };
    }, []);

    // Only treat the title as valid when it was captured for the current URL.
    const effectivePageTitle = pageTitleInfo.url === pageUrl ? pageTitleInfo.title : null;

    // Only enable analytics when user has consent, route is non-admin, and we have a resolved title
    const isAnalyticsEnabled = hasConsent && !isAdminRoute && !!effectivePageTitle;

    // Only initialize analytics if user has consented and route is non-admin
    // hasConsent in config so effect re-runs when granted or revoked
    const { checkStuckUser, trackEvent } = useAnalytics({
        sessionId,
        pageUrl,
        pageTitle: effectivePageTitle || '',
        // Disable analytics entirely on admin routes
        hasConsent: isAnalyticsEnabled,
    });

    useEffect(() => {
        // Only check for stuck users if consent is given and route is non-admin
        if (!isAnalyticsEnabled) {
            return;
        }

        // Check for stuck users every 30 seconds
        const interval = setInterval(() => {
            checkStuckUser().then((result) => {
                if (result?.shouldShowHelp) {
                    // Trigger help chat or show assistance
                    console.log('User appears stuck:', result.triggerReason);
                    // You can integrate with your help chat system here
                    // Example: eventBus.dispatch('showHelpChat', result);
                }
            });
        }, 30000); // Check every 30 seconds

        return () => clearInterval(interval);
    }, [checkStuckUser, isAnalyticsEnabled, location.pathname]);

    // Don't render tracker if no consent or route is admin
    if (!isAnalyticsEnabled) {
        return null;
    }

    return (
        <AnalyticsTracker
            sessionId={sessionId}
            pageUrl={pageUrl}
            pageTitle={effectivePageTitle}
            trackEvent={trackEvent}
        />
    );
};
